import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, map, includes, omit } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import { muiThemeLuma } from 'themes';

const styles = ({
  custom: {
    palette: { colorSupportSteel },
  },
  palette: {
    common: { white },
    text,
  },
}) => ({
  fakeDiv: {
    background: 'transparent',
    border: 'none',
    color: text.primary,
    display: 'inline-block',
    float: 'left',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 400,
    left: 0,
    height: '100%',
    lineHeight: '24px',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 3,
    position: 'absolute',
    width: '100%',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    wordWrap: 'break-word',
    zIndex: 1,
    '-moz-appearance': 'textfield-multiline',
    '-webkit-appearance': 'textarea',
  },
  root: {
    display: 'inline-block',
    resize: 'none',
    position: 'relative',
    width: '100%',
  },
  textArea: {
    background: 'transparent',
    border: `1px solid ${white}`,
    caretColor: text.primary,
    color: 'transparent',
    cursor: 'initial',
    display: 'inline-block',
    float: 'left',
    fontFamily: 'inherit',
    fontFeatureSettings: '"lnum" 1',
    fontSize: 'inherit',
    fontWeight: 400,
    height: '100%',
    lineHeight: '24px',
    outline: 'none',
    padding: 2,
    position: 'relative',
    resize: 'none',
    width: '100%',
    zIndex: 10,
    '&::placeholder': {
      color: `${colorSupportSteel} !important`,
    },
    '&:-ms-input-placeholder': {
      color: `${colorSupportSteel} !important`,
    },
    '&::-webkit-input-placeholder': {
      color: `${colorSupportSteel} !important`,
    },
  },
  disabled: {
    cursor: 'not-allowed',
  },
  disableScroll: {
    overflow: 'hidden',
  },
  expandable: {
    resize: 'vertical',
  },
});

class InputField extends PureComponent {
  componentDidMount() {
    const { classes, name } = this.props;

    this.textArea = document.querySelector(`textarea[name='${name}']`);
    this.fakeDiv = this.textArea.parentElement.querySelector(`.${classes.fakeDiv}`);

    this.textareaInfo = {
      value: '',
      cursorStart: 0,
      cursorEnd: 0,
    };
  }

  handleBlur = (event) => {
    const { onInfoUpdated, onBlur } = this.props;

    this.getCursorPointer(event);

    if (onInfoUpdated) onInfoUpdated(this.textareaInfo);

    if (onBlur) onBlur(event);
  };

  handleChange = (event) => {
    const { onChange, onInfoUpdated } = this.props;

    this.getCursorPointer(event);

    if (onInfoUpdated) onInfoUpdated(this.textareaInfo);

    onChange(event);
  };

  handleClick = (event) => {
    const { onClick, onInfoUpdated } = this.props;

    this.getCursorPointer(event);

    if (onInfoUpdated) onInfoUpdated(this.textareaInfo);

    if (onClick) onClick(event, this.textareaInfo);
  };

  getCursorPointer = (event) => {
    if (event.target.selectionStart !== undefined) {
      this.textareaInfo = {
        value: event.target.value,
        cursorStart: event.target.selectionStart,
        cursorEnd: event.target.selectionEnd,
      };
    } else {
      this.textareaInfo = {
        value: event.target.value,
        cursorStart: 0,
        cursorEnd: 0,
      };
    }
  };

  renderFakeDiv() {
    const { classes, mentions, value, ignoreInvalidSquigglies, squigglyTemplates } = this.props;

    // squigglyTemplateVariables
    const squigglyTemplateVariables = map(squigglyTemplates, (squiggly) => get(squiggly, 'id') || squiggly).join(',');

    // Do some necessary adjustments to render the textarea value inside the div element
    let highlightedValue = value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Parse mentions
    const mentionsVariables = mentions.join('|').replace('.', '\\.');
    if (mentionsVariables) {
      highlightedValue = highlightedValue.replace(
        new RegExp(`\\B(${mentionsVariables})\\b`, 'gm'),
        (tag) => `<span style="color: #3460C5; font-weight: 400;">${tag}</span>`,
      );
    }

    // Parse squigglies
    const invalidSquiggly = (template) =>
      `<span style="border-bottom: 1px solid ${muiThemeLuma.custom.palette.colorDanger}; color: ${
        muiThemeLuma.custom.palette.colorDanger
      }; font-weight: 400;">${template.replace(/¨¨/g, '|')}</span>`;

    const validSquiggly = (template) =>
      `<span style="background-color: #f1f1f1; border-radius: 4px; color: ${
        muiThemeLuma.custom.palette.colorDarkCyan
      }; font-weight: 400;">${template.replace(/¨¨/g, '|')}</span>`;

    if (!ignoreInvalidSquigglies && squigglyTemplateVariables) {
      highlightedValue = highlightedValue.replace(new RegExp(`(${squigglyTemplateVariables})`, 'gm'), validSquiggly);

      highlightedValue = highlightedValue.replace(/{{.*?}\}/gm, (match) => {
        if (
          (includes(squigglyTemplateVariables, match) && !ignoreInvalidSquigglies) ||
          // For Manual Copay, the squiggly is not fixed
          // therefore it should consider the following format valid
          // {{system.url.patientPaymentAmount-XXXXX.XX}}
          // {{system.url.outstandingBalanceChargeUrl}}`;
          // {{system.url.appointmentCopayChargeUrl-${appointmentId}}}`;
          // {{system.url.customBalanceChargeUrl-${amount}}}`;
          highlightedValue.match(
            /{{system.url.(patientPaymentAmount|outstandingBalanceChargeUrl|outstandingBalanceChargeByPracticeUrl|appointmentCopayChargeUrl|customBalanceChargeByPracticeUrl|customBalanceChargeUrl)[-\w]*\}}/g,
          )
        ) {
          return validSquiggly(match);
        }

        return includes(squigglyTemplateVariables, match) ? validSquiggly(match) : invalidSquiggly(match);
      });
    } else {
      // ignoreInvalidSquigglies
      highlightedValue = highlightedValue.replace(/[|]/g, '¨¨').replace(/{{.*?}\}/gm, validSquiggly);
    }

    highlightedValue += '<br />';

    const scrollListener = () => {
      if (this.textArea && this.fakeDiv) this.fakeDiv.scrollTop = this.textArea.scrollTop;
    };

    if (this.textArea) this.textArea.addEventListener('scroll', scrollListener, false);

    return (
      <div
        className={classes.fakeDiv}
        dangerouslySetInnerHTML={{ __html: highlightedValue }} // eslint-disable-line react/no-danger
      />
    );
  }

  renderTextArea() {
    const { classes, disabled, expandable, readOnly, name, value, disableScroll, ...custom } = this.props;

    // Adjust height for readOnly
    const style =
      readOnly && value && this.textArea
        ? { height: `${this.textArea.scrollHeight + 2}px`, overflow: 'hidden' }
        : undefined;

    return (
      <textarea
        className={classnames(classes.textArea, {
          [classes.disabled]: disabled,
          [classes.expandable]: expandable,
          [classes.disableScroll]: disableScroll,
        })}
        style={style}
        disabled={disabled}
        readOnly={readOnly}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onClick={this.handleClick}
        name={name}
        value={value}
        {...omit(custom, [
          'className',
          'ignoreInvalidSquigglies',
          'squigglyTemplates',
          'onBlur',
          'onChange',
          'onClick',
          'onInfoUpdated',
          'mentions',
        ])}
      />
    );
  }

  render() {
    const { classes, className } = this.props;

    return (
      <div className={classnames(className, classes.root)}>
        {this.renderTextArea()}
        {this.renderFakeDiv()}
      </div>
    );
  }
}

InputField.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  disableScroll: PropTypes.bool,
  expandable: PropTypes.bool,
  ignoreInvalidSquigglies: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onInfoUpdated: PropTypes.func,
  mentions: PropTypes.array.isRequired,
  value: PropTypes.string,
  squigglyTemplates: PropTypes.array,
};

InputField.defaultProps = {
  className: '',
  disabled: false,
  expandable: false,
  ignoreInvalidSquigglies: false,
};

export default withStyles(styles)(InputField);
